import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Icon from 'flarum/common/components/Icon';
import GeewenNav from './GeewenNav';
import GeewenFooter from './GeewenFooter';
import { t, tList } from '../helpers/i18n';
import { gs, gsBool, productPrefix } from '../helpers/settings';
import { PRODUCTS, productUrl } from '../products';

/**
 * GeeWenPage.jsx — /geewen 官方页
 *
 * SkyLark Studio 的系统家族官网 (Flarum 2.0 page extension)。
 * 结构: 顶栏 / Hero / 产品矩阵 / 技术底座 / 数据 / 路线图 / 关于 / CTA / 页脚
 *
 * 视觉规范:
 *   - 颜色全部走 CSS 变量 (less/forum.less 里定义 --gs-* token), 浅色深色各一套,
 *     深色由 Flarum 的 [data-theme^=dark] 驱动 ⇒ 跟随论坛自身的主题设置。
 *   - 图标一律 Font Awesome (flarum/common/components/Icon), ⛔ 不用 emoji。
 *   - 中文标题用 Hiragino Sans GB, 西文 Optima, 正文 PingFang SC。
 *
 * 文案来源 (兜底链): 后台设置的 value ⇒ locale 文案。
 *   gs(key, localeText) —— 后台填了就用后台的, 没填就用 locale 的。
 *
 * ⚠️ mithril 0.2.x 的两条硬规则 (踩过两次):
 *   1) ⛔ Flarum 2.0 的 `ItemList` **不是组件** (是工具类, 没有 view 方法),
 *      当 JSX 标签写会被 mithril 当函数调用 → "Class constructor cannot be invoked
 *      without 'new'". ⇒ 列表一律用 map 出来的普通数组。
 *   2) ⛔ 同一个 fragment (数组) 里的 vnode 必须「全有 key」或「全没 key」, 不能混;
 *      且 children 里不能留 null hole (条件渲染要用数组 push, 不能写 {cond ? x : null})。
 *      ⇒ 动态列表每项都带唯一 key, 数组一律包在独立元素里。
 */

/** Hero 右侧的产品堆叠卡 (对应 PRODUCTS 注册表里的两款 + 「更多产品」占位) */
const STACK_TILES = [{
  id: 'scheduler',
  icon: 'fas fa-calendar-check',
  nameKey: 'stack.scheduler_name',
  subKey: 'stack.scheduler_sub',
  tagKey: 'stack.scheduler_tags',
  liveKey: 'stack.live'
}, {
  id: 'dressup',
  icon: 'fas fa-shirt',
  nameKey: 'stack.dressup_name',
  subKey: 'stack.dressup_sub',
  tagKey: 'stack.dressup_tags',
  liveKey: 'stack.live'
}, {
  id: 'more',
  icon: 'fas fa-rocket',
  nameKey: 'stack.more_title',
  subKey: 'stack.more_sub'
}];

/** 堆叠卡 id → PRODUCTS 注册表里的 slug (拿产品的可配置名) */
const STACK_SLUGS = {
  scheduler: 'scheduler',
  dressup: 'dress-up'
};
const PLATFORM_TILES = [{
  icon: 'fas fa-layer-group',
  titleKey: 'platform.core_title',
  descKey: 'platform.core_desc'
}, {
  icon: 'fas fa-user-shield',
  titleKey: 'platform.perm_title',
  descKey: 'platform.perm_desc'
}, {
  icon: 'fas fa-bell',
  titleKey: 'platform.notify_title',
  descKey: 'platform.notify_desc'
}, {
  icon: 'fas fa-circle-half-stroke',
  titleKey: 'platform.theme_title',
  descKey: 'platform.theme_desc'
}, {
  icon: 'fas fa-mobile-screen-button',
  titleKey: 'platform.pwa_title',
  descKey: 'platform.pwa_desc'
}, {
  icon: 'fas fa-plug',
  titleKey: 'platform.plugin_title',
  descKey: 'platform.plugin_desc'
}];
const METRICS = [{
  numKey: 'metrics.metric1_num',
  labelKey: 'metrics.metric1_label',
  subKey: 'metrics.metric1_sub'
}, {
  numKey: 'metrics.metric2_num',
  labelKey: 'metrics.metric2_label',
  subKey: 'metrics.metric2_sub'
}, {
  numKey: 'metrics.metric3_num',
  labelKey: 'metrics.metric3_label',
  subKey: 'metrics.metric3_sub'
}, {
  numKey: 'metrics.metric4_num',
  labelKey: 'metrics.metric4_label',
  subKey: 'metrics.metric4_sub'
}];
const ROADMAP_ITEMS = [{
  icon: 'fas fa-mobile-screen-button',
  titleKey: 'roadmap.item1_title',
  descKey: 'roadmap.item1_desc'
}, {
  icon: 'fas fa-chart-simple',
  titleKey: 'roadmap.item2_title',
  descKey: 'roadmap.item2_desc'
}, {
  icon: 'fas fa-code',
  titleKey: 'roadmap.item3_title',
  descKey: 'roadmap.item3_desc'
}];
const ABOUT_FACTS = [{
  icon: 'fas fa-screwdriver-wrench',
  titleKey: 'about.fact1_title',
  descKey: 'about.fact1_desc'
}, {
  icon: 'fas fa-layer-group',
  titleKey: 'about.fact2_title',
  descKey: 'about.fact2_desc'
}, {
  icon: 'fas fa-rotate',
  titleKey: 'about.fact3_title',
  descKey: 'about.fact3_desc'
}];
export default class GeeWenPage extends Page {
  // 走 vendor Page 的 bodyClass 机制 ($('#app').addClass), 供 less 里做论坛外壳的微调
  bodyClass = 'App--geewen';
  scrollTopOnCreate = false;
  setTitle() {
    app.setTitle(gs('geewen_meta_title', t('meta.title')));
    app.setTitleCount(0);

    // ⚠️ 官网被设成论坛首页时, Flarum 的 updateTitle() 会走 "without_page_title" 模板
    //   (只留论坛名, 见 vendor/flarum/core/js/src/common/Application.tsx L616-630
    //    `onHomepage = m.route.get() === basePath + '/'`) ⇒ 这里把品牌名补回去。
    //   ⛔ 必须在 app.setTitle() **之后** 覆盖 (setTitle 内部会先改 document.title)。
    if (this.onHomepage()) {
      document.title = gs('geewen_meta_title', t('meta.brand_title'));
    }
  }

  /** 当前是不是「论坛首页」(官网被设成首页时就是) */
  onHomepage() {
    try {
      const basePath = app.forum.attribute('basePath') || '';
      return m.route.get() === basePath + '/';
    } catch (e) {
      return false;
    }
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.setTitle();
  }
  sectionHead(labelKey, titleKey, descKey) {
    // ⚠️ 条件分支用数组 push, 不写 {cond ? x : null} (null hole 会让 mithril 报错)
    const children = [m("span", {
      class: "GS-eyebrow"
    }, t(labelKey)), m("h2", {
      class: "GS-section-title"
    }, t(titleKey))];
    if (descKey) {
      children.push(m("p", {
        class: "GS-section-desc"
      }, t(descKey)));
    }
    return m("div", {
      class: "GS-section-head"
    }, children);
  }
  hero() {
    const stackCards = STACK_TILES.map(tile => {
      const slug = STACK_SLUGS[tile.id];
      const prefix = slug ? productPrefix(slug) : null;
      const name = prefix ? gs(prefix + '_name', t(tile.nameKey)) : t(tile.nameKey);
      const sub = prefix ? gs(prefix + '_sub', t(tile.subKey)) : t(tile.subKey);
      const bodyChildren = [m("span", {
        class: "GS-stack-card-name"
      }, name), m("span", {
        class: "GS-stack-card-sub"
      }, sub)];
      if (tile.tagKey) {
        bodyChildren.push(m("span", {
          class: "GS-stack-card-tags"
        }, t(tile.tagKey)));
      }
      const cardChildren = [m("div", {
        class: "GS-stack-card-icon",
        key: "icon"
      }, m(Icon, {
        name: tile.icon
      })), m("div", {
        class: "GS-stack-card-body",
        key: "body"
      }, bodyChildren)];
      if (tile.liveKey) {
        cardChildren.push(m("span", {
          class: "GS-chip GS-chip--live",
          key: "live"
        }, m("span", {
          class: "GS-dot"
        }), t(tile.liveKey)));
      }
      return m("div", {
        class: "GS-stack-card GS-stack-card--" + tile.id,
        key: tile.id
      }, cardChildren);
    });
    const stats = [{
      num: 'hero.stat1_num',
      label: 'hero.stat1_label',
      key: 'geewen_hero_stat1'
    }, {
      num: 'hero.stat2_num',
      label: 'hero.stat2_label',
      key: 'geewen_hero_stat2'
    }, {
      num: 'hero.stat3_num',
      label: 'hero.stat3_label',
      key: 'geewen_hero_stat3'
    }].map(stat => m("li", {
      class: "GS-hero-stat",
      key: stat.num
    }, m("span", {
      class: "GS-hero-stat-num"
    }, gs(stat.key + '_num', t(stat.num))), m("span", {
      class: "GS-hero-stat-label"
    }, gs(stat.key + '_label', t(stat.label)))));
    return m("section", {
      class: "GS-hero",
      id: "gs-top"
    }, m("div", {
      class: "GS-hero-glow GS-hero-glow--blue"
    }), m("div", {
      class: "GS-hero-glow GS-hero-glow--green"
    }), m("div", {
      class: "GS-hero-grid"
    }), m("div", {
      class: "GS-container GS-hero-inner"
    }, m("div", {
      class: "GS-hero-copy"
    }, m("span", {
      class: "GS-badge"
    }, m(Icon, {
      name: "fas fa-layer-group"
    }), gs('geewen_hero_badge', t('hero.badge'))), m("h1", {
      class: "GS-hero-title"
    }, gs('geewen_hero_title', t('hero.title'))), m("p", {
      class: "GS-hero-title-en"
    }, gs('geewen_hero_title_en', t('hero.title_en'))), m("p", {
      class: "GS-hero-subtitle"
    }, gs('geewen_hero_subtitle', t('hero.subtitle'))), m("div", {
      class: "GS-hero-cta"
    }, m("a", {
      class: "GS-btn GS-btn--primary GS-btn--lg",
      href: "#gs-products"
    }, gs('geewen_hero_cta_primary', t('hero.cta_primary')), m(Icon, {
      name: "fas fa-arrow-right"
    })), m("a", {
      class: "GS-btn GS-btn--ghost GS-btn--lg",
      href: "#gs-platform"
    }, gs('geewen_hero_cta_secondary', t('hero.cta_secondary')))), m("div", {
      class: "GS-hero-stats"
    }, m("ul", null, stats))), m("div", {
      class: "GS-hero-stack"
    }, stackCards)));
  }

  /**
   * 产品矩阵。
   *
   * ⛔ 每张卡整体可点 ⇒ 用「标题链接 + ::after 撑满卡片」的 stretched link 做法:
   *   这样整卡可点, 但链接本身仍有可读的文本 (无障碍), 也不会出现嵌套 <a>。
   */
  products() {
    const visible = PRODUCTS.filter(product => gsBool(productPrefix(product.slug) + '_visible', true));
    const cards = visible.map(product => {
      const slug = product.slug;
      const url = productUrl(slug);
      const prefix = productPrefix(slug);
      const highlights = tList('products.' + slug + '.highlights').map(line => m("li", {
        class: "GS-feature",
        key: line
      }, m(Icon, {
        name: "fas fa-check"
      }), m("span", null, line)));
      return m("article", {
        class: "GS-card GS-card--" + product.accent,
        key: slug
      }, m("span", {
        class: "GS-card-accent"
      }), m("header", {
        class: "GS-card-head"
      }, m("span", {
        class: "GS-card-icon"
      }, m(Icon, {
        name: product.icon
      })), m("div", {
        class: "GS-card-titles"
      }, m("h3", {
        class: "GS-card-name"
      }, m("a", {
        href: url
      }, gs(prefix + '_name', t('products.' + slug + '.name')))), m("span", {
        class: "GS-card-sub"
      }, gs(prefix + '_sub', t('products.' + slug + '.sub')))), m("span", {
        class: "GS-chip GS-chip--live"
      }, m("span", {
        class: "GS-dot"
      }), t('stack.live'))), m("p", {
        class: "GS-card-tagline"
      }, gs(prefix + '_tagline', t('products.' + slug + '.tagline'))), m("ul", {
        class: "GS-features"
      }, highlights), m("footer", {
        class: "GS-card-foot"
      }, m("span", {
        class: "GS-link"
      }, t('products.view'), m(Icon, {
        name: "fas fa-arrow-right"
      }))));
    });
    return m("section", {
      class: "GS-section GS-section--alt",
      id: "gs-products"
    }, m("div", {
      class: "GS-container"
    }, this.sectionHead('products.label', 'products.title', 'products.desc'), m("div", {
      class: "GS-grid GS-grid--2"
    }, cards)));
  }
  platform() {
    const tiles = PLATFORM_TILES.map(tile => m("div", {
      class: "GS-tile",
      key: tile.titleKey
    }, m("span", {
      class: "GS-tile-icon"
    }, m(Icon, {
      name: tile.icon
    })), m("h3", {
      class: "GS-tile-title"
    }, t(tile.titleKey)), m("p", {
      class: "GS-tile-desc"
    }, t(tile.descKey))));
    return m("section", {
      class: "GS-section",
      id: "gs-platform"
    }, m("div", {
      class: "GS-container"
    }, m("div", {
      class: "GS-section-head"
    }, m("span", {
      class: "GS-eyebrow"
    }, t('platform.label')), m("h2", {
      class: "GS-section-title"
    }, gs('geewen_platform_title', t('platform.title'))), m("p", {
      class: "GS-section-desc"
    }, t('platform.desc'))), m("div", {
      class: "GS-grid GS-grid--3"
    }, tiles)));
  }
  metrics() {
    const items = METRICS.map(metric => m("li", {
      class: "GS-metric",
      key: metric.numKey
    }, m("span", {
      class: "GS-metric-num"
    }, t(metric.numKey)), m("span", {
      class: "GS-metric-label"
    }, t(metric.labelKey)), m("span", {
      class: "GS-metric-sub"
    }, t(metric.subKey))));
    return m("section", {
      class: "GS-section GS-section--flush"
    }, m("div", {
      class: "GS-container"
    }, m("div", {
      class: "GS-metrics"
    }, m("ul", null, items))));
  }
  roadmap() {
    const items = ROADMAP_ITEMS.map(item => m("div", {
      class: "GS-card GS-card--soon",
      key: item.titleKey
    }, m("header", {
      class: "GS-card-head"
    }, m("span", {
      class: "GS-card-icon"
    }, m(Icon, {
      name: item.icon
    })), m("h3", {
      class: "GS-card-name GS-card-name--sm"
    }, t(item.titleKey)), m("span", {
      class: "GS-chip GS-chip--soon"
    }, t('roadmap.soon'))), m("p", {
      class: "GS-card-tagline"
    }, t(item.descKey))));
    return m("section", {
      class: "GS-section GS-section--alt",
      id: "gs-roadmap"
    }, m("div", {
      class: "GS-container"
    }, m("div", {
      class: "GS-section-head"
    }, m("span", {
      class: "GS-eyebrow"
    }, t('roadmap.label')), m("h2", {
      class: "GS-section-title"
    }, gs('geewen_roadmap_title', t('roadmap.title'))), m("p", {
      class: "GS-section-desc"
    }, t('roadmap.desc'))), m("div", {
      class: "GS-grid GS-grid--3"
    }, items), m("p", {
      class: "GS-roadmap-tail"
    }, m(Icon, {
      name: "fas fa-circle-info"
    }), t('roadmap.tail'))));
  }
  about() {
    const facts = ABOUT_FACTS.map(fact => m("li", {
      class: "GS-fact",
      key: fact.titleKey
    }, m("span", {
      class: "GS-fact-icon"
    }, m(Icon, {
      name: fact.icon
    })), m("div", null, m("strong", {
      class: "GS-fact-title"
    }, t(fact.titleKey)), m("span", {
      class: "GS-fact-desc"
    }, t(fact.descKey)))));
    return m("section", {
      class: "GS-section",
      id: "gs-about"
    }, m("div", {
      class: "GS-container"
    }, m("div", {
      class: "GS-about"
    }, m("div", {
      class: "GS-about-copy"
    }, m("div", {
      class: "GS-section-head"
    }, m("span", {
      class: "GS-eyebrow"
    }, t('about.label')), m("h2", {
      class: "GS-section-title"
    }, gs('geewen_about_title', t('about.title')))), m("p", {
      class: "GS-about-title-en"
    }, t('about.title_en')), m("p", {
      class: "GS-about-text"
    }, t('about.desc1')), m("p", {
      class: "GS-about-text"
    }, t('about.desc2'))), m("div", {
      class: "GS-about-facts"
    }, m("ul", null, facts)))));
  }
  cta() {
    return m("section", {
      class: "GS-section GS-section--flush",
      id: "gs-cta"
    }, m("div", {
      class: "GS-container"
    }, m("div", {
      class: "GS-cta"
    }, m("div", {
      class: "GS-cta-glow"
    }), m("h2", {
      class: "GS-cta-title"
    }, gs('geewen_cta_title', t('cta.title'))), m("p", {
      class: "GS-cta-desc"
    }, gs('geewen_cta_desc', t('cta.desc'))), m("div", {
      class: "GS-cta-actions"
    }, m("a", {
      class: "GS-btn GS-btn--primary GS-btn--lg",
      href: "/"
    }, t('cta.button_primary'), m(Icon, {
      name: "fas fa-arrow-right"
    })), m("a", {
      class: "GS-btn GS-btn--ghost GS-btn--lg",
      href: "#gs-platform"
    }, t('cta.button_secondary'))))));
  }
  view() {
    return m("div", {
      class: "GS-root"
    }, m(GeewenNav, null), m("main", {
      class: "GS-main"
    }, this.hero(), this.products(), this.platform(), this.metrics(), this.roadmap(), this.about(), this.cta()), m(GeewenFooter, null));
  }
}
flarum.reg.add('ziiven-geewen-site', 'forum/components/GeewenPage', GeeWenPage);